import { render } from 'preact-render-to-string';
import { getBrand } from 'shared/campaign-brands';
import { CHECKIN_REACTIONS, CheckinReactionKey, summaryRows } from 'shared/checkin-feedback';
import { BasicEmailWrapper, Line, PrimaryAction } from './shared';

export type CheckinReplyEmailProps = {
  courseTitle: string;
  /** "Tuesday’s standup" or "Weekly summary, week 1". */
  itemLabel: string;
  itemKind: 'standup' | 'summary';
  /** Standups: the student's answers. */
  answers: { prompt: string; answer: string }[];
  /** Summaries: what the student pasted. */
  body: string;
  replies: { authorUserId?: UUID; authorName: string; body: string }[];
  reactions: { coachUserId?: UUID; coachName: string; reaction: CheckinReactionKey }[];
  courseUrl: string;
  unsubscribeUrl?: string;
  brandId?: string;
};

/** "Barb replied to your Tuesday standup", "Barb and Meredith replied to your weekly summary". */
export function checkinReplySubject(opts: {
  replierNames: string[];
  itemKind: 'standup' | 'summary';
  itemDay: string;
}) {
  const names = [...new Set(opts.replierNames)];
  const who =
    names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  const item = opts.itemKind === 'summary' ? 'weekly summary' : `${opts.itemDay} standup`;
  return `${who || 'Your coach'} replied to your ${item}`;
}

const REPLY_CHARACTER_LIMIT = 4000;

function emojis(keys: CheckinReactionKey[]) {
  return CHECKIN_REACTIONS.filter((r) => keys.includes(r.key))
    .map((r) => r.emoji)
    .join(' ');
}

/**
 * The student's email when a coach replies. It reads top to bottom like a
 * thread: what the student wrote, then the coach's reply, then one button.
 * Everything here is plain text from textareas, rendered as escaped JSX.
 */
export function CheckinReplyEmail(props: CheckinReplyEmailProps) {
  const brand = getBrand(props.brandId);
  const rows =
    props.itemKind === 'summary'
      ? summaryRows(props.body).map((r) => ({ label: r.label, value: r.value }))
      : props.answers.map((a) => ({ label: a.prompt, value: a.answer }));
  const replierIds = new Set(props.replies.map((r) => r.authorUserId || r.authorName));
  const reactionsBy = (who: string) =>
    emojis(
      props.reactions.filter((r) => (r.coachUserId || r.coachName) === who).map((r) => r.reaction),
    );
  const reactorsOnly = [
    ...new Map(
      props.reactions
        .filter((r) => !replierIds.has(r.coachUserId || r.coachName))
        .map((r) => [r.coachUserId || r.coachName, r.coachName]),
    ),
  ];

  return render(
    <BasicEmailWrapper
      logoUrl={brand.defaultLogoUrl}
      logoAlt={brand.name}
      copyrightName={brand.copyrightName}
      footer={
        <>
          Reply to this email to reach the coaching team.
          {props.unsubscribeUrl && (
            <>
              {' · '}
              <a
                href={props.unsubscribeUrl}
                style="color:#003DA6;opacity:0.75;"
                target="_blank"
                rel="noreferrer"
              >
                Manage your email preferences
              </a>
            </>
          )}
        </>
      }
    >
      <Line style="color:#636363;font-size:14px;margin-bottom:0.75rem;">
        {props.courseTitle} · {props.itemLabel}
      </Line>
      <div style="margin-bottom:1.5rem;padding:12px 16px;background-color:#f8fafc;border:1px solid #e2e8f0;border-radius:4px;">
        <p style="margin:0 0 0.5rem;font-size:13px;font-weight:600;color:#636363;">You wrote</p>
        <table style="width:100%;border-collapse:collapse;font-size:14px;color:#3f3f46;">
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <th
                  scope="row"
                  style="text-align:left;vertical-align:top;font-weight:600;padding:4px 12px 4px 0;width:35%;"
                >
                  {row.label}
                </th>
                <td style="vertical-align:top;padding:4px 0;white-space:pre-wrap;overflow-wrap:break-word;">
                  {row.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {props.replies.map((reply, i) => {
        const reacted = reactionsBy(reply.authorUserId || reply.authorName);
        const text =
          reply.body.length > REPLY_CHARACTER_LIMIT
            ? `${reply.body.slice(0, REPLY_CHARACTER_LIMIT)}…`
            : reply.body;
        return (
          <div key={i} style="margin-bottom:1.25rem;">
            <p style="margin:0 0 0.25rem;">
              <strong>{reply.authorName}</strong>
              <span style="color:#636363;"> · Coach</span>
              {reacted && <span> {reacted}</span>}
            </p>
            <p style="margin:0;white-space:pre-wrap;overflow-wrap:break-word;">{text}</p>
          </div>
        );
      })}
      {reactorsOnly.map(([who, name]) => (
        <Line key={who} style="color:#636363;font-size:14px;">
          {name} reacted {reactionsBy(who)}
        </Line>
      ))}
      <PrimaryAction href={props.courseUrl}>Open in the course</PrimaryAction>
    </BasicEmailWrapper>,
  );
}
